import { buildApiUrl as buildApiUrlFromConfig } from '../config/api';

// Ré-exporter buildApiUrl pour faciliter son utilisation dans les autres services
export const buildApiUrl = buildApiUrlFromConfig;

const SENSITIVE_CACHE_KEYS = [
  'devis',
  'clients',
  'matieres',
  'fournitures',
  'tarifs_coefficients',
  'tarifs_coefficients_raw',
  'devisCommandes',
  'authToken',
  'user',
  'access',
  'userId',
  'tenantId',
  'matieres_light',
];

export const clearSensitiveCache = () => {
  if (typeof window === 'undefined') return;
  SENSITIVE_CACHE_KEYS.forEach((key) => window.localStorage.removeItem(key));
};

const isFormData = (body: any): body is FormData =>
  typeof FormData !== 'undefined' && body instanceof FormData;

export const getAuthToken = (): string | null => localStorage.getItem('authToken');

export const buildAuthHeaders = (headers?: HeadersInit, body?: any): Headers => {
  const result = new Headers(headers || undefined);
  const token = getAuthToken();

  // Ne jamais propager l'ancien header multi-tenant client
  if (result.has('X-User-ID')) {
    result.delete('X-User-ID');
  }

  if (token && !result.has('Authorization')) {
    result.set('Authorization', `Bearer ${token}`);
  }

  if (!isFormData(body) && !result.has('Content-Type')) {
    result.set('Content-Type', 'application/json');
  }

  return result;
};

const parseErrorMessage = async (response: Response): Promise<string> => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    return data.error || data.message || `HTTP ${response.status}`;
  }

  const text = await response.text().catch(() => '');
  return text || `HTTP ${response.status}`;
};

export const apiFetch = async <T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  const url = buildApiUrl(path);
  const headers = buildAuthHeaders(options.headers, options.body);
  const method = (options.method || 'GET').toString().toUpperCase();
  const relaxedMutationPaths = ['/tarifs', '/matieres', '/mousses', '/fermetures', '/entreprise'];
  const isLightPath = path.startsWith('/light');

  // Garde front basique avant envoi pour les mutations
  if (['POST', 'PUT', 'DELETE'].includes(method) && !path.startsWith('/auth') && !path.startsWith('/subscription/start-trial')) {
    const accessRaw = localStorage.getItem('access');
    if (accessRaw) {
      try {
        const access = JSON.parse(accessRaw);
        const relaxedMutation = relaxedMutationPaths.some((p) => path.startsWith(p));
        const allowed = isLightPath || relaxedMutation ? true : access?.access_mode === 'active' || access?.access_mode === 'trial';
        const pdfException = path.endsWith('/generate-pdf') && access?.access_mode === 'read_only';
        if (!allowed && !pdfException) {
          window.dispatchEvent(
            new CustomEvent('access_denied', {
              detail: { access_mode: access?.access_mode || 'preview', required: relaxedMutation ? 'preview' : 'active', status: 403 },
            })
          );
          const err = new Error('Acces interdit pour cette action');
          (err as any).access_mode = access?.access_mode;
          (err as any).required = relaxedMutation ? 'preview' : 'active';
          (err as any).status = 403;
          throw err;
        }
      } catch {
        /* ignore parsing errors */
      }
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorMessage = await parseErrorMessage(response);
    const error = new Error(errorMessage);
    (error as any).status = response.status;

    if (response.status === 401 || response.status === 403) {
      // Laisser le caller gérer l'expiration/auth manquante sans purge globale
      (error as any).authError = true;
    }

    // Si le backend renvoie une erreur d'acces, propager les infos pour l'UI
    if (response.headers.get('content-type')?.includes('application/json')) {
      try {
        const payload = await response.clone().json();
        if (payload?.error === 'access_denied') {
          (error as any).access_mode = payload.access_mode;
          (error as any).required = payload.required;
          // Dispatch global event pour affichage modal
          if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(
              new CustomEvent('access_denied', {
                detail: {
                  access_mode: payload.access_mode,
                  required: payload.required,
                  status: response.status,
                },
              })
            );
          }
        }
      } catch (_) {
        /* noop */
      }
    }
    throw error;
  }

  const hasBody = response.status !== 204;
  if (!hasBody) return undefined as unknown as T;

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json() as Promise<T>;
  }

  return response.text() as unknown as T;
};
